import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { motion, AnimatePresence } from 'framer-motion'
import { Helmet } from 'react-helmet-async'
import {
    PlusIcon,
    PencilIcon,
    TrashIcon,
    MagnifyingGlassIcon,
    BookOpenIcon,
    ClockIcon,
    CurrencyRupeeIcon
} from '@heroicons/react/24/outline'
import { courseAPI, universityAPI } from '../../services/api'
import LoadingSpinner from '../../components/Common/LoadingSpinner'
import ErrorMessage from '../../components/Common/ErrorMessage'
import CourseForm from '../../components/Admin/CourseForm'
import PermissionWrapper from '../../components/Admin/PermissionWrapper'
import toast from 'react-hot-toast'

const AdminCourses = () => {
    return (
        <PermissionWrapper requiredPermission="courses">
            <AdminCoursesContent />
        </PermissionWrapper>
    )
}

const AdminCoursesContent = () => {
    const [filters, setFilters] = useState({
        search: '',
        level: '',
        status: '',
        featured: 'all',
        page: 1,
        limit: 10,
        sort: 'newest'
    })
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [editingCourse, setEditingCourse] = useState(null)
    const queryClient = useQueryClient()

    const { data, isLoading, error, refetch } = useQuery({
        queryKey: ['admin-courses', filters],
        queryFn: () => courseAPI.getAdminCourses(filters),
        keepPreviousData: true,
    })

    const { data: universities } = useQuery({
        queryKey: ['universities-for-courses'],
        queryFn: () => universityAPI.getUniversities({ limit: 100 }),
    })

    const createMutation = useMutation({
        mutationFn: courseAPI.createCourse,
        onSuccess: () => {
            toast.success('Course created successfully!')
            queryClient.invalidateQueries(['admin-courses'])
            setIsModalOpen(false)
            setEditingCourse(null)
        },
        onError: (error) => {
            toast.error(error.message || 'Failed to create course')
        },
    })

    const updateMutation = useMutation({
        mutationFn: ({ id, data }) => courseAPI.updateCourse(id, data),
        onSuccess: () => {
            toast.success('Course updated successfully!')
            queryClient.invalidateQueries(['admin-courses'])
            setIsModalOpen(false)
            setEditingCourse(null)
        },
        onError: (error) => {
            toast.error(error.message || 'Failed to update course')
        },
    })

    const deleteMutation = useMutation({
        mutationFn: courseAPI.deleteCourse,
        onSuccess: () => {
            toast.success('Course deleted successfully!')
            queryClient.invalidateQueries(['admin-courses'])
        },
        onError: (error) => {
            toast.error(error.message || 'Failed to delete course')
        },
    })

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({
            ...prev,
            [key]: value,
            page: 1
        }))
    }

    const handlePageChange = (newPage) => {
        setFilters(prev => ({ ...prev, page: newPage }))
    }

    const handleEdit = (course) => {
        setEditingCourse(course)
        setIsModalOpen(true)
    }

    const handleDelete = (id) => {
        if (window.confirm('Are you sure you want to delete this course?')) {
            deleteMutation.mutate(id)
        }
    }

    const handleFormSubmit = (data) => {
        if (editingCourse) {
            updateMutation.mutate({ id: editingCourse._id, data })
        } else {
            createMutation.mutate(data)
        }
    }

    const handleCancel = () => {
        setIsModalOpen(false)
        setEditingCourse(null)
    }


    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <ErrorMessage message="Failed to load courses" onRetry={refetch} />
            </div>
        )
    }

    const courses = data?.data?.courses || []
    const pagination = data?.data?.pagination || {}
    const universityList = universities?.data?.universities || []


    return (
        <>
            <Helmet>
                <title>Manage Courses | Margdarshak Admin</title>
                <meta name="robots" content="noindex, nofollow" />
            </Helmet>

            <div className="min-h-screen bg-gray-50">
                {/* Quick Actions */}
                <div className="bg-white shadow-sm border-b border-gray-200">
                    <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4">
                        <div className="flex justify-end">
                            <button
                                onClick={() => setIsModalOpen(true)}
                                className="btn-primary"
                            >
                                <PlusIcon className="w-5 h-5 mr-2" />
                                Add Course
                            </button>
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="bg-white border-b border-gray-200">
                    <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4">
                        <div className="flex flex-col lg:flex-row gap-4">
                            <div className="flex-1">
                                <div className="relative">
                                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                                    <input
                                        type="text"
                                        placeholder="Search courses..."
                                        value={filters.search}
                                        onChange={(e) => handleFilterChange('search', e.target.value)}
                                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
                                    />
                                </div>
                            </div>
                            <div className="flex gap-4">
                                <select
                                    value={filters.level}
                                    onChange={(e) => handleFilterChange('level', e.target.value)}
                                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
                                >
                                    <option value="">All Levels</option>
                                    <option value="Diploma">Diploma</option>
                                    <option value="Undergraduate (UG)">Undergraduate (UG)</option>
                                    <option value="Postgraduate (PG)">Postgraduate (PG)</option>
                                </select>
                                <select
                                    value={filters.status}
                                    onChange={(e) => handleFilterChange('status', e.target.value)}
                                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
                                >
                                    <option value="">All Status</option>
                                    <option value="active">Active</option>
                                    <option value="inactive">Inactive</option>
                                    <option value="draft">Draft</option>
                                </select>
                                <select
                                    value={filters.featured}
                                    onChange={(e) => handleFilterChange('featured', e.target.value)}
                                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
                                >
                                    <option value="all">All Courses</option>
                                    <option value="true">Featured</option>
                                    <option value="false">Not Featured</option>
                                </select>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    {isLoading ? (
                        <div className="flex justify-center py-12">
                            <LoadingSpinner size="lg" />
                        </div>
                    ) : (
                        <>
                            {/* Courses Table */}
                            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Course
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    University
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Degree
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Specialization
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Level
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Duration
                                                </th>

                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Status
                                                </th>
                                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Actions
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {courses.map((course) => {
                                                return (
                                                    <tr key={course._id} className="hover:bg-gray-50">
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <div className="flex items-center">
                                                                <div className="w-12 h-12 bg-secondary-100 rounded-lg flex items-center justify-center mr-4">
                                                                    <BookOpenIcon className="w-6 h-6 text-secondary-600" />
                                                                </div>
                                                                <div>
                                                                    <div className="text-sm font-medium text-gray-900">
                                                                        {course.degreeId ? course.degreeId.name : `${course.level} - ${course.specialization ? course.specialization.join(', ') : 'Course'}`}
                                                                    </div>
                                                                    <div className="text-sm text-gray-500">
                                                                        {course.slug}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <div className="text-sm text-gray-900">
                                                                {course.universityId?.name || 'N/A'}
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <div className="text-sm text-gray-900">
                                                                {course.degreeId?.name || 'N/A'}
                                                            </div>
                                                            <div className="text-sm text-gray-500">
                                                                {course.degreeId?.shortName || ''}
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <div className="text-sm text-gray-900">
                                                                {Array.isArray(course.specialization) ? course.specialization.join(', ') : course.specialization || 'N/A'}
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <div className="text-sm text-gray-900">
                                                                {course.level}
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <div className="flex items-center text-sm text-gray-900">
                                                                <ClockIcon className="w-4 h-4 text-gray-400 mr-1" />
                                                                {course.duration}
                                                            </div>
                                                        </td>

                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${course.status === 'active' ? 'bg-green-100 text-green-800' :
                                                                course.status === 'inactive' ? 'bg-red-100 text-red-800' :
                                                                    'bg-yellow-100 text-yellow-800'
                                                                }`}>
                                                                {course.status}
                                                            </span>
                                                            {course.featured && (
                                                                <div className="mt-1">
                                                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                                                        Featured
                                                                    </span>
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                            <div className="flex justify-end space-x-2">
                                                                <button
                                                                    onClick={() => handleEdit(course)}
                                                                    className="text-accent-600 hover:text-accent-900 p-1"
                                                                    title="Edit Course"
                                                                >
                                                                    <PencilIcon className="w-4 h-4" />
                                                                </button>
                                                                <button
                                                                    onClick={() => handleDelete(course._id)}
                                                                    className="text-red-600 hover:text-red-900 p-1"
                                                                    title="Delete Course"
                                                                >
                                                                    <TrashIcon className="w-4 h-4" />
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            {/* Pagination */}
                            {pagination.totalPages > 1 && (
                                <div className="flex items-center justify-between mt-6">
                                    <div className="text-sm text-gray-700">
                                        Showing {((pagination.currentPage - 1) * pagination.limit) + 1} to {Math.min(pagination.currentPage * pagination.limit, pagination.total)} of {pagination.total} results
                                    </div>
                                    <div className="flex space-x-2">
                                        <button
                                            onClick={() => handlePageChange(pagination.currentPage - 1)}
                                            disabled={pagination.currentPage === 1}
                                            className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            Previous
                                        </button>
                                        <button
                                            onClick={() => handlePageChange(pagination.currentPage + 1)}
                                            disabled={pagination.currentPage === pagination.totalPages}
                                            className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            Next
                                        </button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* Add/Edit Modal */}
                <AnimatePresence>
                    {isModalOpen && (
                        <CourseForm
                            onSubmit={handleFormSubmit}
                            initialData={editingCourse}
                            onCancel={handleCancel}
                            universities={universities?.data?.universities || []}
                        />
                    )}
                </AnimatePresence>
            </div>
        </>
    )
}

export default AdminCourses
